import React from 'react';
import { ImageFormComponent } from '../components/menimagesform';
import { postData } from '../components/misc';
import { trackEvent } from '../utils/bridge.js';

export default function HubbyDraftsAdmin() {
  const [drafts, setDrafts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [message, setMessage] = React.useState('');
  const [draftEdits, setDraftEdits] = React.useState({});

  const loadDrafts = React.useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const response = await fetch('/api/sirri_api/hubby_drafts', {
        credentials: 'include',
      });
      const data = await response.json();
      trackEvent('admin_hubby_drafts_loaded')
      const incomingDrafts = data.drafts || [];
      setDrafts(incomingDrafts);
      setDraftEdits((prev) => {
        const updated = { ...prev };
        incomingDrafts.forEach((draft) => {
          const profile = draft.profile || {};
          if (!updated[draft.id]) {
            updated[draft.id] = { ...profile };
          } else {
            Object.entries(profile).forEach(([key, value]) => {
              if (!(key in updated[draft.id])) {
                updated[draft.id][key] = value;
              }
            });
          }
        });
        return updated;
      });
    } catch (err) {
      console.error('Failed to load hubby drafts', err);
      setMessage('Failed to load hubby drafts.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const confirmDraft = async (draftId) => {
    trackEvent('admin_hubby_draft_confirm_pressed')
    setMessage('');
    try {
      const data = await postData('/api/sirri_api/hubby_drafts/confirm', {
        draft_id: draftId,
      });
      if (data.responsecode === 200) {
        trackEvent('admin_hubby_draft_confirmed')
        setDrafts((prev) => prev.filter((draft) => draft.id !== draftId));
        setMessage('Draft confirmed and moved to data locale.');
      } else {
        setMessage(data.message || 'Failed to confirm draft.');
      }
    } catch (err) {
      console.error('Failed to confirm draft', err);
      setMessage('Failed to confirm draft.');
    }
  };

  const handleEditChange = (draftId, key, value) => {
    trackEvent('admin_hubby_draft_edited', { field: key })
    setDraftEdits((prev) => ({
      ...prev,
      [draftId]: {
        ...(prev[draftId] || {}),
        [key]: value,
      },
    }));
  };

  const saveDraft = async (draftId) => {
    trackEvent('admin_hubby_draft_save_pressed')
    setMessage('');
    const parsedProfile = draftEdits[draftId] || {};

    try {
      const data = await postData('/api/sirri_api/hubby_drafts/update', {
        draft_id: draftId,
        profile: parsedProfile,
      });
      if (data.responsecode === 200) {
        trackEvent('admin_hubby_draft_saved')
        setMessage('Draft saved.');
        loadDrafts();
      } else {
        setMessage(data.message || 'Failed to save draft.');
      }
    } catch (err) {
      console.error('Failed to save draft', err);
      setMessage('Failed to save draft.');
    }
  };

  const deleteDraftImage = async (draftId) => {
    trackEvent('admin_hubby_draft_image_delete_pressed')
    setMessage('');

    try {
      const data = await postData('/api/sirri_api/hubby_drafts/delete_image', {
        draft_id: draftId,
      });

      if (data.responsecode === 200) {
        setMessage('Draft image deleted.');
        loadDrafts();
      } else {
        setMessage(data.message || 'Failed to delete draft image.');
      }
    } catch (err) {
      console.error('Failed to delete draft image', err);
      setMessage('Failed to delete draft image.');
    }
  };

  const deleteDraft = async (draftId) => {
    setMessage('');

    try {
      const data = await postData('/api/sirri_api/hubby_drafts/delete', {
        draft_id: draftId,
      });

      if (data.responsecode === 200) {
        setDrafts((prev) => prev.filter((draft) => draft.id !== draftId));
        setMessage('Draft deleted.');
      } else {
        setMessage(data.message || 'Failed to delete draft.');
      }
    } catch (err) {
      console.error('Failed to delete draft', err);
      setMessage('Failed to delete draft.');
    }
  };

  return (
    <div className="text-white">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h1 className="text-2xl font-semibold">Generated Hubby Drafts</h1>
        <button
          type="button"
          className="px-4 py-2 rounded-full bg-secondary text-white"
          onClick={loadDrafts}
        >
          Refresh
        </button>
      </div>
      {message ? <p className="mb-4 text-yellow-200">{message}</p> : null}
      {loading ? <p>Loading drafts…</p> : null}
      {!loading && drafts.length === 0 ? (
        <p>No draft profiles available.</p>
      ) : null}
      <div className="space-y-6">
        {drafts.map((draft) => {
          const profile = draft.profile || {};
          const fullname = profile.fullname1 || 'Unnamed';
          return (
            <div key={draft.id} className="bg-primary p-4 rounded-lg">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-xl font-semibold">{fullname}</h2>
                  <p className="text-sm text-gray-200">
                    {profile.age1 ? `Age ${profile.age1}` : null}
                    {profile.country1 ? ` • ${profile.country1}` : null}
                  </p>
                </div>
                <span className="text-xs uppercase tracking-wide text-gray-100">
                  {draft.has_image ? 'Image uploaded' : 'Needs image'}
                </span>
              </div>
              <div className="mt-4">
                <ImageFormComponent
                  EMAIL={profile.EMAIL}
                  id={draft.id}
                  fullname1={fullname}
                  allanswers={profile}
                  gender="Male"
                  nextstep={loadDrafts}
                />
                <button
                  type="button"
                  className={`mt-2 px-4 py-2 rounded-full ${
                    draft.has_image ? 'bg-red-600 text-white' : 'bg-gray-500 text-gray-200 cursor-not-allowed'
                  }`}
                  onClick={() => deleteDraftImage(draft.id)}
                  disabled={!draft.has_image}
                >
                  Delete Current Image
                </button>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">
                  Edit draft fields
                </label>
                <div className="space-y-3">
                  {Object.entries(draftEdits[draft.id] || {}).map(([key, value]) => (
                    <div key={`${draft.id}-${key}`} className="flex flex-col">
                      <span className="text-xs uppercase tracking-wide text-gray-200 mb-1">
                        {key}
                      </span>
                      {key === 'futurespouse1' ? (
                        <textarea
                          className="w-full min-h-[160px] text-black p-2 rounded"
                          value={value || ''}
                          onChange={(event) => handleEditChange(draft.id, key, event.target.value)}
                        />
                      ) : (
                        <input
                          className="w-full text-black p-2 rounded"
                          value={value || ''}
                          onChange={(event) => handleEditChange(draft.id, key, event.target.value)}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="mt-2 px-4 py-2 rounded-full bg-blue-500 text-white"
                  onClick={() => saveDraft(draft.id)}
                >
                  Save Draft
                </button>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  className={`px-4 py-2 rounded-full ${
                    draft.has_image ? 'bg-green-500' : 'bg-gray-400 cursor-not-allowed'
                  }`}
                  onClick={() => confirmDraft(draft.id)}
                  disabled={!draft.has_image}
                >
                  Confirm and Move Profile
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-full bg-red-700 text-white"
                  onClick={() => deleteDraft(draft.id)}
                >
                  Delete Draft
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
